import React, { useMemo } from 'react';
import { buildUiHelpers } from './sharedUi';
import NewsletterPreferences from './NewsletterPreferences';
import AccountDataRequest from './AccountDataRequest';

export default function AccountSection({
  COLORS,
  th,
  td,
  inputStyle,
  PLAN_FEATURES,
  FEATURE_TOOLTIPS,
  RSL_WEEKDAYS,
  accountPlan,
  accountPlanStatus,
  subscriptionUntil,
  billingStatus,
  billingCurrentPeriodEnd,
  billingCancelAtPeriodEnd,
  billingAccessUntil,
  billingDeletionScheduledFor,
  billingCustomerId,
  billingHistory,
  billingNotice,
  billingConfig,
  billingActionLoading,
  starteBillingCheckout,
  oeffneBillingPortal,
  canUsePremiumSignalEmails,
  canUseRslNotifications,
  showTradeJournalConfig,
  email,
  tradeJournalLoading,
  tradeJournalAccountLabel,
  setTradeJournalAccountLabel,
  tradeJournalQueryId,
  setTradeJournalQueryId,
  tradeJournalFlexToken,
  setTradeJournalFlexToken,
  tradeJournalFlexTokenSet,
  tradeJournalAgentSecretSet,
  tradeJournalAgentSecretValue,
  tradeJournalAgentGenerating,
  tradeJournalLastSyncAt,
  tradeJournalLastSyncStatus,
  tradeJournalLastSyncError,
  tradeJournalErr,
  tradeJournalMsg,
  tradeJournalSaving,
  speichereTradeJournalKonto,
  generiereTradeJournalAgentSecret,
  emailNotificationsEnabled,
  setEmailNotificationsEnabled,
  premiumSignalEmailsEnabled,
  setPremiumSignalEmailsEnabled,
  rslEmailDays,
  emailTheme,
  setEmailTheme,
  toggleRslEmailDay,
  notificationErr,
  notificationMsg,
  notificationSaving,
  speichereBenachrichtigungen,
  twoFactorEnabled,
  twoFactorPending,
  setTwoFactorEnabled,
  twoFactorCurrentPassword,
  setTwoFactorCurrentPassword,
  twoFactorVerificationCode,
  setTwoFactorVerificationCode,
  twoFactorErr,
  twoFactorMsg,
  twoFactorSaving,
  speichereZweiFaktor,
  bestaetigeZweiFaktor,
  pwCurrent,
  setPwCurrent,
  pwNew,
  setPwNew,
  pwConfirm,
  setPwConfirm,
  pwErr,
  pwMsg,
  pwSaving,
  passwortAendern,
  deletePw,
  newsletterStatus,
  setNewsletterStatus,
  keepNewsletter,
  setKeepNewsletter,
  setDeletePw,
  deleteErr,
  deleteMsg,
  deleteSaving,
  accountLoeschen,
}) {
  const { Card, Btn, Badge } = useMemo(() => buildUiHelpers({ COLORS, th: {} }), [COLORS]);
  const planFeaturesForAccountOverview = useMemo(
    () => PLAN_FEATURES,
    [PLAN_FEATURES],
  );
  const hasBillingPortal = billingConfig?.billing_enabled && billingConfig?.portal_enabled && !!billingCustomerId;
  const formatBillingDateTime = value => {
    if (!value) return '';
    const date = new Date(/[zZ]|[+-]\d\d:\d\d$/.test(value) ? value : `${value}Z`);
    return Number.isNaN(date.getTime()) ? '' : date.toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' });
  };
  const formatBillingAmount = (amountTotal, currency) => {
    if (!Number.isFinite(amountTotal)) return '—';
    const normalizedCurrency = (currency || 'EUR').toUpperCase();
    try {
      return new Intl.NumberFormat('de-DE', { style: 'currency', currency: normalizedCurrency }).format(amountTotal / 100);
    } catch {
      return `${(amountTotal / 100).toFixed(2)} ${normalizedCurrency}`;
    }
  };
  const getBillingProductLabel = (entry) => {
    switch ((entry?.product_key || '').toLowerCase()) {
      case 'basic_monthly':
      case 'basic_yearly':
      case 'essential':
      case 'basic':
        return 'Essential';
      case 'premium_monthly':
      case 'premium_yearly':
      case 'premium':
        return 'Plus';
      case 'coaching':
        return 'Anleitung';
      default:
        return 'Mitgliedschaft';
    }
  };
  const getBillingStatusBadgeColor = status => {
    if (status === 'paid') return 'green';
    if (status === 'payment_failed') return 'red';
    if (status === 'pending') return 'yellow';
    return 'ghost';
  };
  const billingInfoText = (() => {
    if (subscriptionUntil) {
      return `Zugang aktuell bis ${new Date(subscriptionUntil).toLocaleDateString('de-DE')}.`;
    }
    if (billingStatus === 'active' || billingStatus === 'trialing') {
      return 'Dein Stripe-Abo ist aktiv.';
    }
    if (billingStatus === 'incomplete') {
      return 'Stripe hat das Abo angelegt. Die Aktivierung wird noch final synchronisiert.';
    }
    if (billingStatus === 'past_due' || billingStatus === 'unpaid') {
      return 'Es gibt aktuell ein Zahlungsproblem mit deinem Stripe-Abo.';
    }
    if (billingStatus === 'canceled') {
      return 'Dein Stripe-Abo ist gekündigt.';
    }
    if (billingCustomerId) {
      return 'Ein Stripe-Kunde ist hinterlegt, aber es ist noch kein aktives Abo synchronisiert.';
    }
    return 'Aktuell ist kein bezahltes Abo hinterlegt.';
  })();
  const billingStatusLabel = (() => {
    switch ((billingStatus || 'none').toLowerCase()) {
      case 'active':
        return 'aktiv';
      case 'trialing':
        return 'Testphase';
      case 'incomplete':
        return 'offen';
      case 'past_due':
        return 'überfällig';
      case 'unpaid':
        return 'unbezahlt';
      case 'canceled':
        return 'gekündigt';
      default:
        return 'keins';
    }
  })();
  const upgradeContext = (() => {
    if (typeof window === 'undefined') return '';
    try {
      return new URLSearchParams(window.location.search).get('upgrade') || '';
    } catch {
      return '';
    }
  })();
  const showTrialUpgradeFocus = Boolean(
    billingConfig?.billing_enabled && (upgradeContext === 'trial-ending' || accountPlanStatus === 'expired'),
  );

  return (
    <div>
      {accountPlanStatus === 'expired' && !showTrialUpgradeFocus && (
        <Card style={{ width: '100%', maxWidth: '860px', marginBottom: '20px', border: `1px solid ${COLORS.yellow}55` }}>
          <div style={{ color: COLORS.yellow, fontSize: '12px', fontWeight: '700', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '6px' }}>
            Upgrade erforderlich
          </div>
          <div style={{ color: COLORS.muted, fontSize: '14px', lineHeight: 1.7 }}>
            Dein 30-Tage-Test ist abgelaufen. Ohne aktives Abo stehen dir aktuell nur noch Start und Konto zur Verfügung.
          </div>
        </Card>
      )}

      {showTrialUpgradeFocus && (
        <Card
          id="account-upgrade-focus"
          style={{
            width: '100%',
            maxWidth: '860px',
            marginBottom: '20px',
            border: `1px solid ${COLORS.green}66`,
            background: 'linear-gradient(135deg, rgba(19,168,79,0.16), rgba(14,165,233,0.08))',
          }}
        >
          <div style={{ color: COLORS.green, fontSize: '12px', fontWeight: '800', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '8px' }}>
            Zugang verlängern
          </div>
          <h3 style={{ margin: '0 0 8px', fontSize: '20px' }}>Einfach-Trading weiter nutzen</h3>
          <div style={{ color: COLORS.muted, fontSize: '14px', lineHeight: 1.7, marginBottom: '16px' }}>
            Wähle ein Abo, damit Scanner, Depot, Backtests und die weiteren Werkzeuge ohne Unterbrechung verfügbar bleiben.
            Beim Jahresabo zahlst du im Vergleich zum Monatsabo nur 10 statt 12 Monate und sparst dir damit zwei Monate.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: '12px' }}>
            <div style={{ padding: '14px', borderRadius: '14px', border: `1px solid ${COLORS.cyan}44`, background: '#ffffff06' }}>
              <div style={{ color: COLORS.text, fontWeight: '800', marginBottom: '6px' }}>Essential</div>
              <div style={{ color: COLORS.muted, fontSize: '13px', lineHeight: 1.55, marginBottom: '12px' }}>
                RSL-Depot, Scanner, Backtests und Index-Invest für den regelbasierten Einstieg.
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {billingConfig.basic_yearly_available && (
                  <Btn
                    onClick={() => starteBillingCheckout('basic_yearly')}
                    color="ghost"
                    size="sm"
                    style={{ background: COLORS.green, color: '#04170b' }}
                  >
                    {billingActionLoading === 'basic_yearly' ? 'Lade...' : 'Jährlich wählen'}
                  </Btn>
                )}
                {billingConfig.basic_monthly_available && (
                  <Btn
                    onClick={() => starteBillingCheckout('basic_monthly')}
                    color="ghost"
                    size="sm"
                    style={{ border: `1px solid ${COLORS.cyan}55` }}
                  >
                    {billingActionLoading === 'basic_monthly' ? 'Lade...' : 'Monatlich'}
                  </Btn>
                )}
              </div>
            </div>
            <div style={{ padding: '14px', borderRadius: '14px', border: `1px solid ${COLORS.yellow}55`, background: '#ffffff06' }}>
              <div style={{ color: COLORS.text, fontWeight: '800', marginBottom: '6px' }}>Plus</div>
              <div style={{ color: COLORS.muted, fontSize: '13px', lineHeight: 1.55, marginBottom: '12px' }}>
                Zusätzlich Trade Journal, erweiterte Auswertungen und CSV-Export für deine Workflows.
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {billingConfig.premium_yearly_available && (
                  <Btn
                    onClick={() => starteBillingCheckout('premium_yearly')}
                    color="ghost"
                    size="sm"
                    style={{ background: COLORS.green, color: '#04170b' }}
                  >
                    {billingActionLoading === 'premium_yearly' ? 'Lade...' : 'Jährlich wählen'}
                  </Btn>
                )}
                {billingConfig.premium_monthly_available && (
                  <Btn
                    onClick={() => starteBillingCheckout('premium_monthly')}
                    color="ghost"
                    size="sm"
                    style={{ border: `1px solid ${COLORS.yellow}55` }}
                  >
                    {billingActionLoading === 'premium_monthly' ? 'Lade...' : 'Monatlich'}
                  </Btn>
                )}
              </div>
            </div>
          </div>
        </Card>
      )}

      <Card id="account-plan-overview" style={{ width: '100%', maxWidth: '860px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px', marginBottom: '14px', flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, fontSize: '16px' }}>Leistungsübersicht</h3>
        </div>
        <div style={{ overflowX: 'auto', borderRadius: '10px', border: `1px solid ${COLORS.border}` }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: '#ffffff05' }}>
              <tr>
                <th style={th}>Leistung</th>
                <th style={th}>Essential</th>
                <th style={th}>Plus</th>
              </tr>
            </thead>
            <tbody>
              {planFeaturesForAccountOverview.map(feature => (
                <tr key={`konto-${feature.label}`}>
                  <td style={{ ...td, fontWeight: '600', verticalAlign: 'top' }}>
                    {feature.label === 'RSL-Backtest' ? (
                      <span>
                        <span>RSL-Backtest</span>
                        <span style={{ color: COLORS.muted, fontWeight: 600 }}> (Beta, im Aufbau)</span>
                      </span>
                    ) : FEATURE_TOOLTIPS[feature.label] ? (
                      <span
                        title={FEATURE_TOOLTIPS[feature.label]}
                        style={{ cursor: 'help', textDecoration: 'underline dotted', textUnderlineOffset: '3px' }}
                      >
                        {feature.label}
                      </span>
                    ) : (
                      feature.label
                    )}
                  </td>
                  {['basic', 'premium'].map(planKey => {
                    const value = feature[planKey];
                    return (
                      <td key={`konto-${feature.label}-${planKey}`} style={{ ...td, verticalAlign: 'top' }}>
                        {value === true && <span style={{ color: COLORS.green, fontWeight: '700' }}>✓</span>}
                        {value === false && <span style={{ color: COLORS.muted }}>—</span>}
                        {typeof value === 'string' && value.startsWith('✓') && (
                          <span style={{ whiteSpace: 'pre-line', lineHeight: 1.6 }}>
                            <span style={{ color: COLORS.green, fontWeight: '700' }}>✓</span>
                            <span style={{ color: COLORS.muted }}>{value.slice(1)}</span>
                          </span>
                        )}
                        {value !== true && value !== false && !(typeof value === 'string' && value.startsWith('✓')) && (
                          <span style={{ color: COLORS.muted, whiteSpace: 'pre-line', lineHeight: 1.6 }}>{value}</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {billingConfig?.billing_enabled && (
        <Card id="account-billing-section" style={{ width: '100%', maxWidth: '860px', marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px', marginBottom: '14px', flexWrap: 'wrap' }}>
            <h3 style={{ margin: 0, fontSize: '16px' }}>💳 Mitgliedschaft & Billing</h3>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
              <Badge color={accountPlan === 'premium' ? 'yellow' : accountPlan === 'basic' ? 'cyan' : 'ghost'}>
                {accountPlan === 'premium' ? 'Plus' : accountPlan === 'basic' ? 'Essential' : 'Free'}
              </Badge>
              <Badge color={billingStatus === 'active' ? 'green' : billingStatus === 'canceled' ? 'yellow' : billingStatus === 'past_due' || billingStatus === 'unpaid' ? 'red' : 'ghost'}>
                Billing: {billingStatusLabel}
              </Badge>
            </div>
          </div>
          {billingNotice && (
            <div style={{ marginBottom: '14px', padding: '12px 14px', borderRadius: '12px', background: '#164e6344', border: `1px solid ${COLORS.cyan}44`, color: COLORS.cyan, fontSize: '13px', lineHeight: 1.6 }}>
              {billingNotice}
            </div>
          )}
          <div style={{ color: COLORS.muted, fontSize: '13px', lineHeight: 1.7, marginBottom: '14px' }}>
            {billingInfoText}
            {billingCurrentPeriodEnd && billingStatus !== 'past_due' && billingStatus !== 'unpaid' && (
              <span> Stripe-Periode bis {new Date(billingCurrentPeriodEnd).toLocaleDateString('de-DE')}.</span>
            )}
            {billingCancelAtPeriodEnd && (
              <span> Kündigung zum Periodenende ist vorgemerkt.</span>
            )}
            {(billingStatus === 'past_due' || billingStatus === 'unpaid') && billingAccessUntil && (
              <span> Bezahlzugang bis {formatBillingDateTime(billingAccessUntil)}. Bitte prüfe deine Zahlungsmethode.</span>
            )}
            {billingDeletionScheduledFor && (
              <span> Deine App-Daten sind zur Löschung am {formatBillingDateTime(billingDeletionScheduledFor)} vorgesehen. Vorher erhältst du eine gesonderte E-Mail.</span>
            )}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: hasBillingPortal ? '12px' : 0 }}>
            {billingConfig.basic_monthly_available && (
              <Btn
                onClick={() => starteBillingCheckout('basic_monthly')}
                color="ghost"
                size="sm"
                style={{ background: COLORS.cyan, color: '#052728' }}
              >
                {billingActionLoading === 'basic_monthly' ? 'Lade...' : 'Essential monatlich'}
              </Btn>
            )}
            {billingConfig.basic_yearly_available && (
              <Btn
                onClick={() => starteBillingCheckout('basic_yearly')}
                color="ghost"
                size="sm"
                style={{ background: COLORS.cyan, color: '#052728' }}
              >
                {billingActionLoading === 'basic_yearly' ? 'Lade...' : 'Essential jährlich · 2 Monate sparen'}
              </Btn>
            )}
            {billingConfig.premium_monthly_available && (
              <Btn
                onClick={() => starteBillingCheckout('premium_monthly')}
                color="ghost"
                size="sm"
                style={{ background: COLORS.yellow, color: '#3f2a06' }}
              >
                {billingActionLoading === 'premium_monthly' ? 'Lade...' : 'Plus monatlich'}
              </Btn>
            )}
            {billingConfig.premium_yearly_available && (
              <Btn
                onClick={() => starteBillingCheckout('premium_yearly')}
                color="ghost"
                size="sm"
                style={{ background: COLORS.yellow, color: '#3f2a06' }}
              >
                {billingActionLoading === 'premium_yearly' ? 'Lade...' : 'Plus jährlich · 2 Monate sparen'}
              </Btn>
            )}
          </div>
          {billingConfig.coaching_available && (
            <div
              style={{
                marginTop: '14px',
                marginBottom: hasBillingPortal ? '12px' : 0,
                padding: '14px 16px',
                borderRadius: '14px',
                border: `1px solid ${COLORS.green}44`,
                background: '#13a84f14',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '14px',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div>
                <div style={{ color: COLORS.text, fontWeight: '800', marginBottom: '5px' }}>
                  Persönliches Coaching
                </div>
                <div style={{ color: COLORS.muted, fontSize: '13px', lineHeight: 1.6 }}>
                  1:1-Termin für Einrichtung, RSL-Workflow, App-Fragen und den sauberen Start mit deinem Depot.
                  Nach der Buchung melden wir uns per E-Mail zur Terminabstimmung.
                </div>
              </div>
              <Btn onClick={() => starteBillingCheckout('coaching')} color="green" size="sm">
                {billingActionLoading === 'coaching' ? 'Lade...' : 'Coaching-Termin buchen'}
              </Btn>
            </div>
          )}
          {hasBillingPortal && (
            <div
              style={{
                marginTop: '12px',
                padding: '14px 16px',
                borderRadius: '14px',
                border: `1px solid ${COLORS.border}`,
                background: '#ffffff04',
              }}
            >
              <div style={{ color: COLORS.text, fontWeight: '800', marginBottom: '6px' }}>
                Abo verwalten
              </div>
              <div style={{ color: COLORS.muted, fontSize: '13px', lineHeight: 1.65, marginBottom: '10px' }}>
                Verwalte dein Abonnement über das Kundenportal von Stripe:
                <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>
                  <li>Zahlungsmethode ändern</li>
                  <li>Rechnungen ansehen</li>
                  <li>Abo ändern oder kündigen</li>
                </ul>
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <Btn onClick={oeffneBillingPortal} color="ghost" size="sm">
                  {billingActionLoading === 'portal' ? 'Lade...' : 'Abo verwalten'}
                </Btn>
              </div>
            </div>
          )}
          <div style={{ marginTop: '18px', borderTop: `1px solid ${COLORS.border}`, paddingTop: '16px' }}>
            <div style={{ fontSize: '13px', fontWeight: '700', letterSpacing: '0.06em', textTransform: 'uppercase', color: COLORS.muted, marginBottom: '12px' }}>
              Zahlungsverlauf
            </div>
            {billingHistory?.length ? (
              <div style={{ display: 'grid', gap: '10px', maxHeight: '280px', overflowY: 'auto', paddingRight: '4px' }}>
                {billingHistory.map(entry => (
                  <div
                    key={entry.id}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(0, 1.2fr) minmax(0, 1fr) auto auto',
                      gap: '10px',
                      alignItems: 'center',
                      padding: '12px 14px',
                      borderRadius: '12px',
                      background: '#ffffff05',
                      border: `1px solid ${COLORS.border}`,
                    }}
                  >
                    <div>
                      <div style={{ color: COLORS.text, fontSize: '14px', fontWeight: '700' }}>
                        {getBillingProductLabel(entry)}
                      </div>
                      <div style={{ color: COLORS.muted, fontSize: '12px', marginTop: '4px' }}>
                        {new Date(entry.paid_at || entry.created_at).toLocaleDateString('de-DE')}
                      </div>
                    </div>
                    <div style={{ color: COLORS.text, fontSize: '14px', fontWeight: '700' }}>
                      {formatBillingAmount(entry.amount_total, entry.currency)}
                    </div>
                    <Badge color={getBillingStatusBadgeColor(entry.status)}>
                      {entry.status === 'paid' ? 'Bezahlt' : entry.status === 'payment_failed' ? 'Fehlgeschlagen' : entry.status}
                    </Badge>
                    {entry.hosted_invoice_url ? (
                      <a
                        href={entry.hosted_invoice_url}
                        target="_blank"
                        rel="noreferrer"
                        style={{ color: COLORS.cyan, fontSize: '12px', fontWeight: '700', textDecoration: 'none' }}
                      >
                        Rechnung
                      </a>
                    ) : (
                      <span style={{ color: COLORS.muted, fontSize: '12px' }}>—</span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ color: COLORS.muted, fontSize: '13px' }}>
                Noch keine Zahlungen vorhanden.
              </div>
            )}
          </div>
        </Card>
      )}

      {showTradeJournalConfig && (
        <Card style={{ width: '100%', maxWidth: '860px', marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px', marginBottom: '14px', flexWrap: 'wrap' }}>
            <h3 style={{ margin: 0, fontSize: '16px' }}>📝 Trade Journal</h3>
            <div style={{ color: COLORS.muted, fontSize: '12px' }}>
              {tradeJournalLastSyncAt
                ? `Letzter Sync: ${new Date(tradeJournalLastSyncAt).toLocaleString('de-DE')}`
                : 'Noch kein Sync'}
            </div>
          </div>
          <div style={{ color: COLORS.muted, fontSize: '13px', lineHeight: 1.7, marginBottom: '14px' }}>
            Hinterlege hier Deine IBKR Flex Query fuer den laufenden XML-Import sowie Deinen Flex Token. Der Sync zieht genau diese eine Query und dedupliziert spaetere Imports ueber `ibExecID`.
          </div>
          <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>
            Kontobezeichnung
          </label>
          <input
            style={inputStyle}
            type="text"
            value={tradeJournalAccountLabel}
            onChange={e => setTradeJournalAccountLabel(e.target.value)}
            placeholder="Interactive Brokers-U1234567"
          />
          <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>
            Query ID
          </label>
          <input
            style={inputStyle}
            type="text"
            inputMode="numeric"
            value={tradeJournalQueryId}
            onChange={e => setTradeJournalQueryId(e.target.value)}
            placeholder="Last 30 Calendar Days"
          />
          <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>
            Flex Token
          </label>
          <input
            style={inputStyle}
            type="password"
            value={tradeJournalFlexToken}
            onChange={e => setTradeJournalFlexToken(e.target.value)}
            placeholder={tradeJournalFlexTokenSet ? 'Gespeichertes Token bleibt unverändert' : 'Flex Token eingeben'}
          />
          <div style={{ color: COLORS.muted, fontSize: '12px', lineHeight: 1.7, marginBottom: '12px' }}>
            {tradeJournalFlexTokenSet
              ? 'Ein Flex Token ist bereits gespeichert. Wenn Du das Feld leer lässt, bleibt der bestehende Token erhalten.'
              : 'Der Flex Token wird verschlüsselt gespeichert.'}
          </div>
          <div
            style={{
              marginBottom: '16px',
              padding: '14px 16px',
              borderRadius: '12px',
              border: `1px solid ${COLORS.border}`,
              background: '#ffffff05',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '10px' }}>
              <div style={{ fontSize: '14px', fontWeight: '700', color: COLORS.text }}>Einfach-Trading.de Live Agent</div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <Badge color={tradeJournalAgentSecretSet ? 'green' : 'ghost'}>
                  {tradeJournalAgentSecretSet ? 'Secret gesetzt' : 'Noch kein Secret'}
                </Badge>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', marginBottom: tradeJournalAgentSecretValue ? '10px' : 0 }}>
              <Btn onClick={generiereTradeJournalAgentSecret} color="green">
                {tradeJournalAgentGenerating
                  ? 'Erzeuge Secret...'
                  : tradeJournalAgentSecretSet
                    ? 'Secret neu generieren'
                    : 'Secret erzeugen'}
              </Btn>
              <div style={{ color: COLORS.muted, fontSize: '12px', lineHeight: 1.6 }}>
                Das Secret wird aus Sicherheitsgründen nur direkt nach dem Erzeugen vollständig angezeigt.
              </div>
            </div>
            {tradeJournalAgentSecretValue ? (
              <>
                <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>
                  Agent-Secret
                </label>
                <input
                  style={inputStyle}
                  type="text"
                  readOnly
                  value={tradeJournalAgentSecretValue}
                />
              </>
            ) : null}
          </div>
          <details
            style={{
              marginBottom: '16px',
              padding: '14px 16px',
              borderRadius: '12px',
              border: `1px solid ${COLORS.border}`,
              background: '#ffffff05',
            }}
          >
            <summary style={{ fontSize: '14px', fontWeight: '700', color: COLORS.text, cursor: 'pointer' }}>
              Anleitung: XML-Datei für den Jahres-Import erstellen
            </summary>
            <div style={{ color: COLORS.muted, fontSize: '13px', lineHeight: 1.7, marginTop: '10px' }}>
              Diese Anleitung ist für den manuellen Jahres-Import einer XML-Datei aus dem IBKR Client Portal gedacht.
            </div>
            <ol style={{ margin: '12px 0 0 18px', padding: 0, color: COLORS.text, fontSize: '13px', lineHeight: 1.8 }}>
              <li>
                Öffne im IBKR Client Portal den Bereich <strong>Performance &amp; Reports</strong> und gehe auf <strong>Flex Queries</strong>.
              </li>
              <li>
                Klicke auf <strong>Create</strong> und wähle <strong>Activity Flex Query</strong>.
              </li>
              <li>
                Vergib einen Namen, z. B. <strong>Einfach-Trading-Trade-Journal</strong>.
              </li>
              <li>
                Aktiviere auf der Übersichtsseite genau diese Bereiche:
                <div style={{ color: COLORS.muted, marginTop: '4px' }}>
                  <strong>An:</strong> Account Information, Cash Transactions, Commission Details, Forex P/L Details, Option Exercises, Assignments and Expirations, Realized and Unrealized Performance Summary in Base, Trades.
                </div>
                <div style={{ color: COLORS.muted }}>
                  <strong>Aus:</strong> alle anderen Bereiche, z. B. Cash Report, Mark-to-Market Performance Summary in Base, Month &amp; Year to Date Performance Summary in Base, Net Asset Value (NAV) in Base, Open Positions, Corporate Actions, Transaction Fees.
                </div>
              </li>
              <li>
                Öffne die aktivierten Bereiche nacheinander und setze sie genau so:
                <div style={{ marginTop: '8px', color: COLORS.muted }}>
                  <div><strong style={{ color: COLORS.text }}>Account Information</strong></div>
                  <div>- Account ID</div>
                  <div>- Currency</div>
                  <div>- Select All: aus</div>
                </div>
                <div style={{ marginTop: '8px', color: COLORS.muted }}>
                  <div><strong style={{ color: COLORS.text }}>Cash Transactions</strong></div>
                  <div>- Dividends</div>
                  <div>- Payment in Lieu of Dividends</div>
                  <div>- Withholding Tax</div>
                  <div>- 871(m) Withholding</div>
                  <div>- Advisor Fees</div>
                  <div>- Other Fees</div>
                  <div>- Other Income</div>
                  <div>- Deposits &amp; Withdrawals</div>
                  <div>- Carbon Credits</div>
                  <div>- Bill Pay</div>
                  <div>- Broker Interest Paid</div>
                  <div>- Broker Interest Received</div>
                  <div>- Broker Fees</div>
                  <div>- Bond Interest Paid</div>
                  <div>- Bond Interest Received</div>
                  <div>- Price Adjustments</div>
                  <div>- Commission Adjustments</div>
                  <div>- Detail</div>
                  <div>- Summary: aus</div>
                  <div>- Select All: an</div>
                </div>
                <div style={{ marginTop: '8px', color: COLORS.muted }}>
                  <div><strong style={{ color: COLORS.text }}>Commission Details</strong></div>
                  <div>- Select All: an</div>
                </div>
                <div style={{ marginTop: '8px', color: COLORS.muted }}>
                  <div><strong style={{ color: COLORS.text }}>Forex P/L Details</strong></div>
                  <div>- Transaction</div>
                  <div>- Closed Lots: aus</div>
                  <div>- Select All: an</div>
                </div>
                <div style={{ marginTop: '8px', color: COLORS.muted }}>
                  <div><strong style={{ color: COLORS.text }}>Option Exercises, Assignments and Expirations</strong></div>
                  <div>- Select All: an</div>
                </div>
                <div style={{ marginTop: '8px', color: COLORS.muted }}>
                  <div><strong style={{ color: COLORS.text }}>Realized and Unrealized Performance Summary in Base</strong></div>
                  <div>- Select All: an</div>
                </div>
                <div style={{ marginTop: '8px', color: COLORS.muted }}>
                  <div><strong style={{ color: COLORS.text }}>Trades</strong></div>
                  <div>- Symbol Summary</div>
                  <div>- Asset Class</div>
                  <div>- Execution</div>
                  <div>- Order: aus</div>
                  <div>- Closed Lots: aus</div>
                  <div>- Wash Sales: aus</div>
                  <div>- Select All: an</div>
                </div>
              </li>
              <li>
                In der <strong>Delivery Configuration</strong> setze:
                <div style={{ color: COLORS.muted, marginTop: '4px' }}>
                  Format <strong>XML</strong>, Period <strong>Last 30 Calendar Days</strong> fuer den taeglichen Sync.
                </div>
              </li>
              <li>
                In der <strong>General Configuration</strong> setze:
                <div style={{ color: COLORS.muted, marginTop: '4px' }}>
                  Date Format <strong>dd/MM/yyyy</strong>, Time Format <strong>HHmmss</strong>, Date/Time Separator <strong>;</strong>, Profit and Loss <strong>Default</strong>.
                </div>
                <div style={{ color: COLORS.muted }}>
                  Include Canceled Trades <strong>No</strong>, Include Currency Rates <strong>Yes</strong>, Include Audit Trail Fields <strong>Yes</strong>, Display Account Alias in Place of Account ID <strong>No</strong>, Breakout by Day <strong>No</strong>.
                </div>
              </li>
              <li>
                Klicke auf <strong>Continue</strong> und speichere die Query.
              </li>
              <li>
                Für den Jahres-Import wähle beim Ausführen der Query:
                <div style={{ color: COLORS.muted, marginTop: '4px' }}>
                  Period <strong>Custom Date Range</strong>, From Date <strong>01.01.JJJJ</strong>, To Date <strong>31.12.JJJJ</strong>, Format <strong>XML</strong>.
                </div>
              </li>
              <li>
                Klicke auf <strong>Run</strong>, lade die XML-Datei herunter und importiere sie im Trade Journal über <strong>Datei importieren</strong>.
              </li>
            </ol>
          </details>
          <details
            style={{
              marginBottom: '16px',
              padding: '14px 16px',
              borderRadius: '12px',
              border: `1px solid ${COLORS.border}`,
              background: '#ffffff05',
            }}
          >
            <summary style={{ fontSize: '14px', fontWeight: '700', color: COLORS.text, cursor: 'pointer' }}>
              Anleitung: Sync Query und Flex Token erstellen und hinterlegen
            </summary>
            <div style={{ color: COLORS.muted, fontSize: '13px', lineHeight: 1.7, marginTop: '10px' }}>
              Diese Anleitung ist für den laufenden Sync gedacht. Die Query liefert den rollierenden XML-Report für den manuellen IBKR-Sync in unserem Trade Journal.
            </div>
            <ol style={{ margin: '12px 0 0 18px', padding: 0, color: COLORS.text, fontSize: '13px', lineHeight: 1.8 }}>
              <li>
                Öffne im IBKR Client Portal den Bereich <strong>Performance &amp; Reports</strong> und gehe auf <strong>Flex Queries</strong>.
              </li>
              <li>
                Klicke auf <strong>Create</strong> und wähle <strong>Activity Flex Query</strong>.
              </li>
              <li>
                Vergib einen Namen, z. B. <strong>Einfach-Trading-Sync</strong>.
              </li>
              <li>
                Aktiviere auf der Übersichtsseite genau diese Bereiche:
                <div style={{ color: COLORS.muted, marginTop: '4px' }}>
                  <strong>An:</strong> Cash Report, Cash Transactions, Trades.
                </div>
                <div style={{ color: COLORS.muted }}>
                  <strong>Aus:</strong> alle anderen Bereiche.
                </div>
              </li>
              <li>
                Öffne die aktivierten Bereiche nacheinander und setze sie genau so:
                <div style={{ marginTop: '8px', color: COLORS.muted }}>
                  <div><strong style={{ color: COLORS.text }}>Cash Report</strong></div>
                  <div>- Base Currency Summary</div>
                  <div>- Currency Breakout</div>
                  <div>- Exclude segments and MTD/YTD breakout: aus</div>
                  <div>- Account ID</div>
                  <div>- Currency</div>
                  <div>- Level of Detail</div>
                  <div>- From Date</div>
                  <div>- To Date</div>
                  <div>- Starting Cash</div>
                  <div>- Ending Cash</div>
                  <div>- Ending Settled Cash</div>
                  <div>- Net Cash Balance (SLB)</div>
                  <div>- Select All: aus</div>
                </div>
                <div style={{ marginTop: '8px', color: COLORS.muted }}>
                  <div><strong style={{ color: COLORS.text }}>Cash Transactions</strong></div>
                  <div>- Dividends</div>
                  <div>- Payment in Lieu of Dividends</div>
                  <div>- Withholding Tax</div>
                  <div>- 871(m) Withholding</div>
                  <div>- Advisor Fees</div>
                  <div>- Other Fees</div>
                  <div>- Other Income</div>
                  <div>- Deposits &amp; Withdrawals</div>
                  <div>- Carbon Credits</div>
                  <div>- Bill Pay</div>
                  <div>- Broker Interest Paid</div>
                  <div>- Broker Interest Received</div>
                  <div>- Broker Fees</div>
                  <div>- Bond Interest Paid</div>
                  <div>- Bond Interest Received</div>
                  <div>- Price Adjustments</div>
                  <div>- Commission Adjustments</div>
                  <div>- Detail</div>
                  <div>- Summary: aus</div>
                  <div>- Select All: an</div>
                </div>
                <div style={{ marginTop: '8px', color: COLORS.muted }}>
                  <div><strong style={{ color: COLORS.text }}>Trades</strong></div>
                  <div>- Execution</div>
                  <div>- Symbol Summary: aus</div>
                  <div>- Asset Class: aus</div>
                  <div>- Order: aus</div>
                  <div>- Closed Lots: aus</div>
                  <div>- Wash Sales: aus</div>
                  <div>- Select All: an</div>
                </div>
              </li>
              <li>
                In der <strong>Delivery Configuration</strong> setze:
                <div style={{ color: COLORS.muted, marginTop: '4px' }}>
                  Format <strong>XML</strong>, Period <strong>Last 30 Calendar Days</strong>.
                </div>
              </li>
              <li>
                In der <strong>General Configuration</strong> setze:
                <div style={{ color: COLORS.muted, marginTop: '4px' }}>
                  Date Format <strong>yyyyMMdd</strong>, Time Format <strong>HHmmss</strong>, Date/Time Separator <strong>;</strong>, Profit and Loss <strong>Default</strong>.
                </div>
                <div style={{ color: COLORS.muted }}>
                  Include Canceled Trades <strong>No</strong>, Include Currency Rates <strong>No</strong>, Include Audit Trail Fields <strong>Yes</strong>, Display Account Alias in Place of Account ID <strong>No</strong>, Breakout by Day <strong>No</strong>.
                </div>
              </li>
              <li>
                Klicke auf <strong>Continue</strong> und speichere die Query.
              </li>
              <li>
                Nach dem Speichern siehst Du oben in IBKR die <strong>Query ID</strong>. Trage diese bei uns im Konto im Bereich <strong>Trade Journal</strong> in das Feld <strong>Query ID</strong> ein.
              </li>
              <li>
                Öffne danach in IBKR die <strong>Flex Web Service Configuration</strong> und prüfe, dass der <strong>Flex Web Service Status</strong> auf <strong>Enabled</strong> steht.
              </li>
              <li>
                Klicke auf <strong>Configure</strong>. Dort siehst Du den aktuellen Token oder kannst über <strong>Generate New Token</strong> einen neuen Token erzeugen. Klicke anschließend auf <strong>Save</strong>.
              </li>
              <li>
                Trage diesen Token bei uns im Konto im Bereich <strong>Trade Journal</strong> in das Feld <strong>Flex Token</strong> ein und klicke auf <strong>Trade Journal speichern</strong>. Danach kannst Du im Trade Journal den Button <strong>IBKR Sync</strong> verwenden.
              </li>
            </ol>
          </details>
          {(tradeJournalLastSyncStatus || tradeJournalLastSyncError) && (
            <div style={{ color: COLORS.muted, fontSize: '12px', lineHeight: 1.7, marginBottom: '12px' }}>
              Status: {tradeJournalLastSyncStatus || '—'}
              {tradeJournalLastSyncError ? ` • Letzter Fehler: ${tradeJournalLastSyncError}` : ''}
            </div>
          )}
          {tradeJournalErr && <div style={{ color: COLORS.red, fontSize: '13px', marginBottom: '12px' }}>⚠ {tradeJournalErr}</div>}
          {tradeJournalMsg && <div style={{ color: COLORS.green, fontSize: '13px', marginBottom: '12px' }}>✓ {tradeJournalMsg}</div>}
          <Btn onClick={speichereTradeJournalKonto} color="green">
            {tradeJournalLoading || tradeJournalSaving ? 'Speichere...' : 'Trade Journal speichern'}
          </Btn>
        </Card>
      )}

      <div style={{ width: '100%', maxWidth: '860px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))', gap: '20px', marginBottom: '20px' }}>
          <Card style={{ marginBottom: 0 }}>
            <h3 style={{ margin: '0 0 14px', fontSize: '15px' }}>Benachrichtigungen</h3>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginBottom: '12px' }}>
              <input
                type="checkbox"
                checked={emailNotificationsEnabled}
                onChange={e => setEmailNotificationsEnabled(e.target.checked)}
                style={{ width: '16px', height: '16px', accentColor: COLORS.accent }}
              />
              <span style={{ fontSize: '14px' }}>E-Mail-Benachrichtigungen generell aktivieren</span>
            </label>
            {canUsePremiumSignalEmails && (
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginBottom: '16px' }}>
                <input
                  type="checkbox"
                  checked={premiumSignalEmailsEnabled}
                  onChange={e => setPremiumSignalEmailsEnabled(e.target.checked)}
                  disabled={!emailNotificationsEnabled}
                  style={{ width: '16px', height: '16px', accentColor: COLORS.accent }}
                />
                <span style={{ fontSize: '14px', color: emailNotificationsEnabled ? COLORS.text : COLORS.muted }}>
                  Plus-Signale-Dienst aktivieren
                </span>
              </label>
            )}
            {canUseRslNotifications ? (
              <>
                <div style={{ fontSize: '12px', color: COLORS.muted, marginBottom: '8px' }}>
                  RSL-Benachrichtigung um 08:30 an ausgewählten Wochentagen
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                  {RSL_WEEKDAYS.map(day => {
                    const active = rslEmailDays.includes(day.id);
                    return (
                      <button
                        key={day.id}
                        type="button"
                        onClick={() => toggleRslEmailDay(day.id)}
                        disabled={!emailNotificationsEnabled}
                        style={{
                          padding: '7px 12px',
                          borderRadius: '999px',
                          border: `1px solid ${active ? COLORS.accent : COLORS.border}`,
                          background: active ? COLORS.accent : 'transparent',
                          color: active ? '#fff' : (emailNotificationsEnabled ? COLORS.muted : '#6b7f8d'),
                          cursor: emailNotificationsEnabled ? 'pointer' : 'not-allowed',
                          fontSize: '12px',
                          fontWeight: '700',
                        }}
                      >
                        {day.label}
                      </button>
                    );
                  })}
                </div>
                <div style={{ color: COLORS.muted, fontSize: '12px', lineHeight: 1.7, marginBottom: '12px' }}>
                  Es wird der aktuelle Zustand deines individuellen RSL-Depots per E-Mail versendet. Keine Auswahl bedeutet: keine RSL-Depot-Mail.
                </div>
              </>
            ) : (
              <div style={{ color: COLORS.muted, fontSize: '12px', lineHeight: 1.7, marginBottom: '12px' }}>
                Für den RSL-Signale-Dienst ist mindestens eine Essential-Mitgliedschaft notwendig.
              </div>
            )}
            <div style={{ fontSize: '12px', color: COLORS.muted, marginBottom: '8px' }}>
              E-Mail-Darstellung
            </div>
            <div style={{ display: 'inline-flex', gap: '6px', padding: '4px', borderRadius: '999px', border: `1px solid ${COLORS.border}`, background: '#ffffff05', marginBottom: '12px' }}>
              <button
                type="button"
                onClick={() => setEmailTheme('dark')}
                style={{
                  padding: '6px 12px',
                  borderRadius: '999px',
                  border: 'none',
                  background: emailTheme === 'dark' ? COLORS.accent : 'transparent',
                  color: emailTheme === 'dark' ? '#fff' : COLORS.muted,
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '700',
                }}
              >
                Dunkel
              </button>
              <button
                type="button"
                onClick={() => setEmailTheme('light')}
                style={{
                  padding: '6px 12px',
                  borderRadius: '999px',
                  border: 'none',
                  background: emailTheme === 'light' ? COLORS.accent : 'transparent',
                  color: emailTheme === 'light' ? '#fff' : COLORS.muted,
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '700',
                }}
              >
                Hell
              </button>
            </div>
            {notificationErr && <div style={{ color: COLORS.red, fontSize: '13px', marginBottom: '12px' }}>⚠ {notificationErr}</div>}
            {notificationMsg && <div style={{ color: COLORS.green, fontSize: '13px', marginBottom: '12px' }}>✓ {notificationMsg}</div>}
            <Btn onClick={speichereBenachrichtigungen} color="green">
              {notificationSaving ? 'Speichere...' : 'Benachrichtigungen speichern'}
            </Btn>
          </Card>

          <Card style={{ marginBottom: 0 }}>
            <h3 style={{ margin: '0 0 14px', fontSize: '15px' }}>Zwei-Faktor-Authentifizierung</h3>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginBottom: '12px' }}>
              <input
                type="checkbox"
                checked={twoFactorEnabled}
                onChange={e => setTwoFactorEnabled(e.target.checked)}
                style={{ width: '16px', height: '16px', accentColor: COLORS.accent }}
              />
              <span style={{ fontSize: '14px' }}>2FA für zukünftige Logins aktivieren</span>
            </label>
            <div style={{ color: COLORS.muted, fontSize: '12px', lineHeight: 1.7, marginBottom: '12px' }}>
              Vor der Aktivierung wird ein 6-stelliger Bestätigungscode per E-Mail gesendet. Erst nach erfolgreicher Code-Eingabe ist 2FA aktiv.
            </div>
            <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>
              Aktuelles Passwort
            </label>
            <input
              style={inputStyle}
              type="password"
              autoComplete="current-password"
              value={twoFactorCurrentPassword}
              onChange={e => setTwoFactorCurrentPassword(e.target.value)}
              placeholder="Zur Bestätigung eingeben"
            />
            {twoFactorPending && (
              <>
                <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginTop: '12px', marginBottom: '6px' }}>
                  Bestätigungscode aus E-Mail
                </label>
                <input
                  style={inputStyle}
                  type="text"
                  autoComplete="one-time-code"
                  value={twoFactorVerificationCode}
                  onChange={e => setTwoFactorVerificationCode(e.target.value)}
                  placeholder="6-stelligen Code eingeben"
                />
              </>
            )}
            {twoFactorErr && <div style={{ color: COLORS.red, fontSize: '13px', marginBottom: '12px' }}>⚠ {twoFactorErr}</div>}
            {twoFactorMsg && <div style={{ color: COLORS.green, fontSize: '13px', marginBottom: '12px' }}>✓ {twoFactorMsg}</div>}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <Btn onClick={speichereZweiFaktor} color="green">
                {twoFactorSaving ? 'Speichere...' : '2FA speichern'}
              </Btn>
              {twoFactorPending && (
                <Btn onClick={bestaetigeZweiFaktor} color="accent">
                  {twoFactorSaving ? 'Prüfe...' : 'Code bestätigen'}
                </Btn>
              )}
            </div>
          </Card>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))', gap: '20px' }}>
          <Card style={{ marginBottom: 0 }}>
            <h3 style={{ margin: '0 0 14px', fontSize: '15px' }}>Passwort ändern</h3>
            <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>
              Aktuelles Passwort
            </label>
            <input style={inputStyle} type="password" autoComplete="current-password" value={pwCurrent} onChange={e => setPwCurrent(e.target.value)} />
            <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>
              Neues Passwort
            </label>
            <input style={inputStyle} type="password" autoComplete="new-password" value={pwNew} onChange={e => setPwNew(e.target.value)} />
            <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>
              Neues Passwort wiederholen
            </label>
            <input style={inputStyle} type="password" autoComplete="new-password" value={pwConfirm} onChange={e => setPwConfirm(e.target.value)} />
            {pwErr && <div style={{ color: COLORS.red, fontSize: '13px', marginBottom: '12px' }}>⚠ {pwErr}</div>}
            {pwMsg && <div style={{ color: COLORS.green, fontSize: '13px', marginBottom: '12px' }}>✓ {pwMsg}</div>}
            <Btn onClick={passwortAendern} color="green">
              {pwSaving ? 'Speichere...' : 'Passwort speichern'}
            </Btn>
          </Card>

          <Card style={{ marginBottom: 0 }}>
            <NewsletterPreferences COLORS={COLORS} onStatusChange={setNewsletterStatus} refreshKey={newsletterStatus} />
          </Card>
        </div>

        <div style={{ marginTop: '20px' }}>
          <Card style={{ marginBottom: 0 }}>
            <h3 style={{ margin: '0 0 14px', fontSize: '15px' }}>Account löschen</h3>
            <AccountDataRequest COLORS={COLORS} />
            <div style={{ color: COLORS.muted, fontSize: '13px', lineHeight: 1.7, marginBottom: '12px' }}>
              Dein Account wird deaktiviert und kann 30 Tage lang durch erneute Registrierung mit derselben E-Mail-Adresse wiederhergestellt werden. Danach werden deine App-Nutzungsdaten einschließlich Depot und IB-Journal gelöscht. Erforderliche Abrechnungs- und Nachweisdaten werden getrennt aufbewahrt. Dein Newsletter wird ebenfalls beendet, sofern du ihn nicht ausdrücklich behalten möchtest.
            </div>
            {newsletterStatus === 'subscribed' && <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, marginBottom: 12 }}>
              <input type="checkbox" checked={keepNewsletter} onChange={e => setKeepNewsletter(e.target.checked)} />
              Meinen bereits bestätigten Newsletter nach der Account-Löschung behalten.
            </label>}
            <label style={{ fontSize: '12px', color: COLORS.muted, display: 'block', marginBottom: '6px' }}>
              Aktuelles Passwort
            </label>
            <input style={inputStyle} type="password" autoComplete="current-password" value={deletePw} onChange={e => setDeletePw(e.target.value)} />
            {deleteErr && <div style={{ color: COLORS.red, fontSize: '13px', marginBottom: '12px' }}>⚠ {deleteErr}</div>}
            {deleteMsg && <div style={{ color: COLORS.green, fontSize: '13px', marginBottom: '12px' }}>✓ {deleteMsg}</div>}
            <Btn onClick={accountLoeschen} color="red">
              {deleteSaving ? 'Deaktiviere...' : 'Account löschen'}
            </Btn>
          </Card>
        </div>
      </div>
    </div>
  );
}
